import { z } from 'zod';
import { CimdQuintileSchema, CommunityCimdSchema } from './cimd';

/**
 * W.1 — Wellness & Headcount keystone object contracts.
 *
 * Per operator-cop-doctrine.md §8.3 decision (a), the single highest-leverage
 * missing object. A `community` is the keystone entity; `community_headcounts`
 * are point-in-time accounting snapshots (evacuated/remaining/accounted/
 * unreachable), and `community_wellness_checks` track warming-centre + vulnerable-
 * resident contact status through a planned→in_progress→complete|gap_identified
 * lifecycle. Headcounts + wellness checks optionally tie to a SITREP v3.1
 * incident + operational period; anchored SITREPs roll them up to close ICS-209
 * blocks 31 + 34 (§9.1).
 */

/* ------------------------------------------------------------------ *
 * Enums
 * ------------------------------------------------------------------ */

export const CommunityTypeSchema = z.enum([
  'municipality',
  'first_nation',
  'unincorporated',
  'metis_settlement',
  'inuit_community',
  'other',
]);
export type CommunityType = z.infer<typeof CommunityTypeSchema>;

/** Provenance of a headcount snapshot. */
export const HeadcountSourceSchema = z.enum([
  'operator',
  'community_official',
  'mutual_aid',
  'agency_report',
  'estimate',
]);
export type HeadcountSource = z.infer<typeof HeadcountSourceSchema>;

export const WellnessCheckStatusSchema = z.enum([
  'planned',
  'in_progress',
  'complete',
  'gap_identified',
]);
export type WellnessCheckStatus = z.infer<typeof WellnessCheckStatusSchema>;

/* ------------------------------------------------------------------ *
 * Community — requests
 * ------------------------------------------------------------------ */

/** POST /api/communities — declare a community. geography is [lon, lat]. */
export const CommunityCreateSchema = z.object({
  name: z.string().min(1).max(256),
  community_type: CommunityTypeSchema,
  jurisdiction: z.string().max(256).optional(),
  lon: z.number().min(-180).max(180),
  lat: z.number().min(-90).max(90),
  population: z.number().int().nonnegative().nullable().optional(),
  vulnerable_population: z.number().int().nonnegative().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
  session_id: z.string().min(1).max(128),
});
export type CommunityCreate = z.infer<typeof CommunityCreateSchema>;

/** PATCH /api/communities/:id — edit attributes. All editable fields optional;
 *  geography moves via lon+lat together. session_id is required, not a field. */
export const CommunityPatchSchema = z
  .object({
    name: z.string().min(1).max(256).optional(),
    community_type: CommunityTypeSchema.optional(),
    jurisdiction: z.string().max(256).nullable().optional(),
    lon: z.number().min(-180).max(180).optional(),
    lat: z.number().min(-90).max(90).optional(),
    population: z.number().int().nonnegative().nullable().optional(),
    vulnerable_population: z.number().int().nonnegative().nullable().optional(),
    metadata: z.record(z.unknown()).optional(),
    session_id: z.string().min(1).max(128),
  })
  .superRefine((val, ctx) => {
    // lon/lat must move together (a centroid is a pair).
    if ((val.lon === undefined) !== (val.lat === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'lon and lat must be provided together',
        path: ['lon'],
      });
    }
    // Require at least one editable field beyond session_id.
    const { session_id: _sid, ...rest } = val;
    if (Object.values(rest).every((v) => v === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'at least one editable field is required',
        path: ['name'],
      });
    }
  });
export type CommunityPatch = z.infer<typeof CommunityPatchSchema>;

/* ------------------------------------------------------------------ *
 * Community — responses
 * ------------------------------------------------------------------ */

export const CommunitySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    community_type: CommunityTypeSchema,
    jurisdiction: z.string().nullable(),
    lon: z.number(),
    lat: z.number(),
    population: z.number().int().nullable(),
    vulnerable_population: z.number().int().nullable(),
    metadata: z.record(z.unknown()),
    created_at: z.string(),
    /** Soft-retire flag (W.2 editor): null = active, ISO timestamp = retired-at. */
    retired_at: z.string().nullable(),
  })
  /** StatCan CIMD 2021 enrichment block (migration 0068, task #234). */
  .merge(CommunityCimdSchema);
export type Community = z.infer<typeof CommunitySchema>;

/* ------------------------------------------------------------------ *
 * Headcount — request + response
 * ------------------------------------------------------------------ */

/** POST /api/communities/:id/headcounts — declare a snapshot. */
export const HeadcountCreateSchema = z.object({
  incident_id: z.string().uuid().nullable().optional(),
  operational_period_id: z.string().uuid().nullable().optional(),
  /** Effective time of the snapshot; defaults to now(). Lets an operator backfill
   *  a reported "as of 0600" figure (and disambiguates ordering of same-instant
   *  snapshots). */
  as_of: z.string().datetime().optional(),
  evacuated: z.number().int().nonnegative().default(0),
  remaining: z.number().int().nonnegative().default(0),
  accounted_for: z.number().int().nonnegative().default(0),
  unreachable: z.number().int().nonnegative().default(0),
  /** #462 — a first-class split of `remaining`: how many of those still in the
   *  community are awaiting an aircraft out (northern air-evac). NULLABLE ON PURPOSE
   *  — omit / null = "not broken out / unknown", DISTINCT from 0 = "none awaiting a
   *  flight" (unknown≠zero). Not coerced to 0 by a default. */
  awaiting_airlift: z.number().int().nonnegative().nullable().optional(),
  source: HeadcountSourceSchema,
  declared_by_operator_id: z.string().max(128).optional(),
  notes: z.string().max(2000).optional(),
  session_id: z.string().min(1).max(128),
});
export type HeadcountCreate = z.infer<typeof HeadcountCreateSchema>;

export const CommunityHeadcountSchema = z.object({
  id: z.string(),
  community_id: z.string(),
  incident_id: z.string().nullable(),
  operational_period_id: z.string().nullable(),
  as_of: z.string(),
  evacuated: z.number().int(),
  remaining: z.number().int(),
  accounted_for: z.number().int(),
  unreachable: z.number().int(),
  /** #462 — split of `remaining` awaiting an air-lift; NULL = unknown (≠ 0). */
  awaiting_airlift: z.number().int().nullable(),
  source: HeadcountSourceSchema,
  declared_by_operator_id: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
});
export type CommunityHeadcount = z.infer<typeof CommunityHeadcountSchema>;

/* ------------------------------------------------------------------ *
 * Wellness check — request + response
 * ------------------------------------------------------------------ */

/** POST /api/communities/:id/wellness-checks — create a wellness check. */
export const WellnessCheckCreateSchema = z.object({
  incident_id: z.string().uuid().nullable().optional(),
  status: WellnessCheckStatusSchema.default('planned'),
  warming_centres_active: z.number().int().nonnegative().default(0),
  contact_attempts_made: z.number().int().nonnegative().default(0),
  vulnerable_residents_contacted: z.number().int().nonnegative().default(0),
  vulnerable_residents_unreachable: z.number().int().nonnegative().default(0),
  declared_by_operator_id: z.string().max(128).optional(),
  notes: z.string().max(2000).optional(),
  session_id: z.string().min(1).max(128),
});
export type WellnessCheckCreate = z.infer<typeof WellnessCheckCreateSchema>;

/** PATCH /api/wellness-checks/:id — update status + metrics. The status lifecycle
 *  is enforced server-side (planned→in_progress→complete|gap_identified). */
export const WellnessCheckPatchSchema = z
  .object({
    status: WellnessCheckStatusSchema.optional(),
    warming_centres_active: z.number().int().nonnegative().optional(),
    contact_attempts_made: z.number().int().nonnegative().optional(),
    vulnerable_residents_contacted: z.number().int().nonnegative().optional(),
    vulnerable_residents_unreachable: z.number().int().nonnegative().optional(),
    notes: z.string().max(2000).nullable().optional(),
    session_id: z.string().min(1).max(128),
  })
  .superRefine((val, ctx) => {
    const { session_id: _sid, ...rest } = val;
    if (Object.values(rest).every((v) => v === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'at least one updatable field is required',
        path: ['status'],
      });
    }
  });
export type WellnessCheckPatch = z.infer<typeof WellnessCheckPatchSchema>;

export const CommunityWellnessCheckSchema = z.object({
  id: z.string(),
  community_id: z.string(),
  incident_id: z.string().nullable(),
  status: WellnessCheckStatusSchema,
  warming_centres_active: z.number().int(),
  contact_attempts_made: z.number().int(),
  vulnerable_residents_contacted: z.number().int(),
  vulnerable_residents_unreachable: z.number().int(),
  last_updated_at: z.string(),
  declared_by_operator_id: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
});
export type CommunityWellnessCheck = z.infer<typeof CommunityWellnessCheckSchema>;

/* ------------------------------------------------------------------ *
 * Composite responses
 * ------------------------------------------------------------------ */

/** GET /api/communities/:id — detail with the latest headcount + wellness check. */
export const CommunityDetailSchema = CommunitySchema.extend({
  latest_headcount: CommunityHeadcountSchema.nullable(),
  latest_wellness_check: CommunityWellnessCheckSchema.nullable(),
});
export type CommunityDetail = z.infer<typeof CommunityDetailSchema>;

export const CommunityHeadcountListResponseSchema = z.object({
  headcounts: z.array(CommunityHeadcountSchema),
});
export type CommunityHeadcountListResponse = z.infer<typeof CommunityHeadcountListResponseSchema>;

export const CommunityWellnessCheckListResponseSchema = z.object({
  wellness_checks: z.array(CommunityWellnessCheckSchema),
});
export type CommunityWellnessCheckListResponse = z.infer<
  typeof CommunityWellnessCheckListResponseSchema
>;

/** Compact headcount roll-up carried on the W.1 list feature (W.2.1, Task #91).
 *  Mirrors the four accounting buckets of the latest headcount snapshot. */
export const HeadcountSummarySchema = z.object({
  evacuated: z.number().int(),
  remaining: z.number().int(),
  accounted_for: z.number().int(),
  unreachable: z.number().int(),
});
export type HeadcountSummary = z.infer<typeof HeadcountSummarySchema>;

/** GET /api/communities — lightweight GeoJSON FeatureCollection for map render
 *  (name, type, population for the bubble; geometry is the centroid).
 *
 *  W.2.1 (Task #91) additively folds the latest wellness status + a headcount
 *  summary onto each feature so the W.2 map paints coloured on first paint,
 *  instead of the grey-then-recolor pattern that needed a per-community detail
 *  fetch. Both are nullable (null = no check / no headcount yet). */
export const CommunityFeaturePropsSchema = z.object({
  id: z.string(),
  name: z.string(),
  community_type: CommunityTypeSchema,
  jurisdiction: z.string().nullable(),
  population: z.number().int().nullable(),
  vulnerable_population: z.number().int().nullable(),
  /** Status of the most recent wellness check, or null if none exists. */
  latest_wellness_status: WellnessCheckStatusSchema.nullable(),
  /** The four buckets of the most recent headcount snapshot, or null if none. */
  latest_headcount_summary: HeadcountSummarySchema.nullable(),
  /** CIMD 2021 headline quintile (Situational Vulnerability) for the map
   *  shading sub-toggle (task #234) — null = honest unknown. The full
   *  four-dimension block rides the detail endpoint, not the bbox list. */
  cimd_situational_vulnerability_q: CimdQuintileSchema.nullable(),
});
export type CommunityFeatureProps = z.infer<typeof CommunityFeaturePropsSchema>;

/* ------------------------------------------------------------------ *
 * Arrival attestation (#645) — the HOST link, cut at the PII line
 * ------------------------------------------------------------------ */

/** POST /api/communities/:hostId/arrival-attestations — a HOST community's governing
 *  agency (or the PEOC) records an AGGREGATE, ATTRIBUTED, NON-PII count of evacuees who
 *  checked in at its reception ("312 members attested at Timmins, 14:20"). It NEVER writes
 *  the sending community's headcount — the two registers sit beside each other and
 *  reconcile to NOTHING. NO per-person/PII field exists (name/family/medical/dietary) —
 *  the PII line is the scope boundary (ESS/Red Cross own registration & inquiry, App A #29). */
export const ArrivalAttestationCreateSchema = z.object({
  sending_community_id: z.string().uuid(),
  /** The AGGREGATE head-count who checked in at the host's door. NON-negative. */
  persons: z.number().int().nonnegative(),
  /** When the arrival was observed at the host's door; defaults to now() if omitted. */
  attested_at: z.string().datetime().optional(),
  incident_id: z.string().uuid().nullable().optional(),
  notes: z.string().max(2000).optional(),
  session_id: z.string().min(1).max(128),
});
export type ArrivalAttestationCreate = z.infer<typeof ArrivalAttestationCreateSchema>;

export const ArrivalAttestationSchema = z.object({
  id: z.string(),
  sending_community_id: z.string(),
  sending_community_name: z.string().nullable(),
  host_community_id: z.string(),
  host_community_name: z.string().nullable(),
  persons: z.number().int(),
  attested_by_agency_id: z.string(),
  attested_by_agency_name: z.string().nullable(),
  attested_at: z.string(),
  incident_id: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
});
export type ArrivalAttestation = z.infer<typeof ArrivalAttestationSchema>;

/** GET /api/communities/:id/arrival-attestations — BOTH registers for a community:
 *  `as_host` = arrivals attested AT this community; `as_sending` = this community's own
 *  people attested at hosts. Shown SIDE BY SIDE; a disagreement with the community's own
 *  headcount is a FACT (rendered UNRECONCILED), never averaged, never resolved to
 *  "accounted for". `total_attested_arrivals` is NULL when NO host has attested (absence
 *  ≠ 0 — the app renders "not reported by <host>", never 0). */
export const ArrivalAttestationsResponseSchema = z.object({
  as_host: z.array(ArrivalAttestationSchema),
  as_sending: z.array(ArrivalAttestationSchema),
  total_attested_arrivals: z.number().int().nullable(),
});
export type ArrivalAttestationsResponse = z.infer<typeof ArrivalAttestationsResponseSchema>;
